interface WebsiteValueSectionProps {
  onOpenForm: () => void;
}

const features = [
  { icon: 'ri-palette-line', title: 'Modern 2026 Design', desc: 'Premium aesthetics that build instant credibility. Looks like a $10K custom build.' },
  { icon: 'ri-smartphone-line', title: 'Mobile-First, Lightning Fast', desc: 'Fully responsive with sub-2-second load times — critical for rankings and conversions.' },
  { icon: 'ri-cursor-line', title: 'Built to Convert', desc: 'Every page element — CTAs, forms, layout — engineered to turn visitors into paying customers.' },
  { icon: 'ri-robot-line', title: 'Structured for AI + Voice', desc: 'Schema markup and content architecture that AI tools can read, cite, and recommend.' },
  { icon: 'ri-shield-check-line', title: 'Professional, High-Trust Look', desc: 'Clean design that signals authority — so customers choose you, not a competitor.' },
];

export default function WebsiteValueSection({ onOpenForm }: WebsiteValueSectionProps) {
  return (
    <section className="bg-white py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left */}
          <div>
            <p className="text-emerald-500 text-xs font-bold tracking-widest uppercase mb-4">The Website</p>
            <h2 className="text-3xl md:text-5xl font-black text-[#0d1117] leading-tight mb-6">
              A Website Designed to Convert —{' '}
              <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
                Not Just Exist
              </span>
            </h2>

            <p className="text-[#6b7280] text-base md:text-lg leading-relaxed mb-10">
              Most websites are digital brochures collecting dust. Yours will be a lead-generation machine — engineered to rank, engage, and convert.
            </p>

            <div className="space-y-4 mb-10">
              {features.map((f) => (
                <div key={f.title} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 flex-shrink-0 mt-0.5 group-hover:from-emerald-100 transition-colors duration-200">
                    <i className={`${f.icon} text-emerald-600`} style={{ fontSize: '18px' }}></i>
                  </div>
                  <div>
                    <p className="text-[#0d1117] font-semibold text-sm mb-0.5">{f.title}</p>
                    <p className="text-[#9ca3af] text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quote block */}
            <div className="bg-gradient-to-r from-[#0d1117] to-[#0d2420] rounded-xl px-6 py-5 mb-8">
              <p className="text-white font-semibold text-base md:text-lg">
                &ldquo;Your website becomes your best salesperson.&rdquo;
              </p>
              <p className="text-white/45 text-sm mt-1">Working 24/7 — generating leads while you sleep.</p>
            </div>

            {/* Dominant CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onOpenForm}
                className="whitespace-nowrap bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-base px-8 py-4 rounded-full hover:opacity-90 hover:scale-[1.02] transition-all duration-200 cursor-pointer flex items-center gap-3"
              >
                See What Your Site Could Look Like
                <i className="ri-arrow-right-line" style={{ fontSize: '18px' }}></i>
              </button>
              <a
                href="tel:8004559670"
                className="whitespace-nowrap flex items-center justify-center gap-2 border border-[#e5e7eb] text-[#374151] font-semibold text-base px-6 py-4 rounded-full hover:border-emerald-300 hover:text-emerald-700 transition-all duration-200 cursor-pointer"
              >
                <i className="ri-phone-fill text-emerald-500" style={{ fontSize: '16px' }}></i>
                Call 800-455-9670
              </a>
            </div>
            <p className="text-[#9ca3af] text-xs mt-3">Free preview. No obligation. Ready in 48 hours.</p>
          </div>

          {/* Right — Mockup */}
          <div className="relative flex justify-center">
            <div className="relative w-full max-w-lg">
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-100/50 to-teal-100/50 rounded-3xl blur-2xl pointer-events-none"></div>
              <div className="relative rounded-2xl overflow-hidden border border-[#e5e7eb]">
                <img
                  src="https://public.readdy.ai/ai/img_res/4758d100c47af40a250c40337e68e895.jpg"
                  alt="Modern website mockup on multiple devices"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-white border border-[#e5e7eb] rounded-xl px-4 py-3 shadow-lg">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-50">
                    <i className="ri-speed-up-line text-emerald-600" style={{ fontSize: '17px' }}></i>
                  </div>
                  <div>
                    <p className="text-[#0d1117] text-xs font-bold">&lt;2s Load Time</p>
                    <p className="text-[#9ca3af] text-xs">Fully optimized</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -left-4 bg-white border border-[#e5e7eb] rounded-xl px-4 py-3 shadow-lg">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-50">
                    <i className="ri-smartphone-line text-emerald-600" style={{ fontSize: '17px' }}></i>
                  </div>
                  <div>
                    <p className="text-[#0d1117] text-xs font-bold">Mobile-First</p>
                    <p className="text-[#9ca3af] text-xs">All screen sizes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
